import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Footer() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!form.name.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email) || !form.message.trim()) {
      toast.error("Fill in all fields with a valid email");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/custom-request`, form);
      setSent(true);
      toast.success("Request sent — I'll be in touch.");
    } catch {
      toast.error("Could not send. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <footer id="about" data-testid="footer" className="border-t border-white/10 bg-black/30 backdrop-blur-sm">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32 grid lg:grid-cols-2 gap-16">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <span className="text-[#2E6BE6] text-xs uppercase tracking-[0.3em]">Off the grid?</span>
          <h2 className="font-display font-bold uppercase tracking-tighter text-4xl md:text-6xl mt-4 leading-[0.9]">
            Need a custom time?
          </h2>
          <p className="text-white/50 mt-6 max-w-md font-body">
            Weekend sessions, group work, or a block booking outside 9–5? Drop the details and I'll sort a slot that fits.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="space-y-5"
          data-testid="custom-request-form"
        >
          {sent ? (
            <div className="border border-[#D4AF37]/40 bg-[#D4AF37]/5 p-8">
              <p className="font-display font-bold uppercase text-2xl text-[#D4AF37]">Request received</p>
              <p className="text-white/60 mt-2">Thanks {form.name.split(" ")[0]} — check your inbox soon.</p>
            </div>
          ) : (
            <>
              <input
                data-testid="custom-name"
                placeholder="Name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full bg-transparent border-b border-white/20 py-3 outline-none focus:border-[#D4AF37] transition-colors placeholder:text-white/25"
              />
              <input
                data-testid="custom-email"
                placeholder="Email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full bg-transparent border-b border-white/20 py-3 outline-none focus:border-[#D4AF37] transition-colors placeholder:text-white/25"
              />
              <textarea
                data-testid="custom-message"
                placeholder="What are you after?"
                rows={3}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="w-full bg-transparent border-b border-white/20 py-3 outline-none focus:border-[#D4AF37] transition-colors resize-none placeholder:text-white/25"
              />
              <button
                data-testid="custom-submit"
                onClick={submit}
                disabled={busy}
                className="bg-[#D4AF37] text-black px-7 py-3.5 text-sm uppercase tracking-[0.15em] font-semibold hover:-translate-y-0.5 transition-transform disabled:opacity-50"
              >
                {busy ? "Sending…" : "Send request →"}
              </button>
            </>
          )}
        </motion.div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-8 flex flex-col md:flex-row items-center justify-between gap-4 text-white/40 text-xs uppercase tracking-[0.2em]">
          <span className="font-display font-bold text-white">JG<span className="text-[#2E6BE6]">/</span>COACHING</span>
          <span>© {new Date().getFullYear()} — All rights reserved</span>
        </div>
      </div>
    </footer>
  );
}
